import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useState } from 'react';

import { Layout } from '@myaccount/layout';

import './styles.css';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
});

function CustomApp({ Component, pageProps }: AppProps) {
	const [isSkipFocused, setIsSkipFocused] = useState(false);

	return (
		<>
			<Head>
				<title>Manchester City Football Club.</title>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0"
				/>
				<meta
					name="description"
					content="The Official Website of Manchester City F.C. Get the latest Man City news, injury updates, fixtures, player signings, match highlights &amp; much more!"
				/>
			</Head>

			<button
				id="focus-sentinel"
				tabIndex={-1}
				aria-hidden="true"
				style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					padding: 0,
					margin: 0,
					overflow: 'hidden',
					border: 'none',
				}}
			/>
			<button
				style={{
					position: isSkipFocused ? 'static' : 'absolute',
					color: isSkipFocused ? '#000' : 'unset',
					textDecoration: isSkipFocused ? 'underline' : 'none',
					left: isSkipFocused ? 'unset' : '-9999px',
					top: isSkipFocused ? 'unset' : 'auto',
					border: 'none',
					outline: 'none',
				}}
				onFocus={() => setIsSkipFocused(true)}
				onBlur={() => setIsSkipFocused(false)}
				onClick={() => {
					const focusableSelector = [
						'a[href]',
						'button',
						'input',
						'select',
						'textarea',
						'[tabindex]:not([tabindex="-1"])',
					].join(',');
					const $main = document.querySelector(
						'#main'
					) as HTMLElement | null;
					const focusableElements =
						$main &&
						(Array.from(
							$main.querySelectorAll(focusableSelector)
						) as HTMLElement[] | null);

					if (!focusableElements) return;

					for (const el of focusableElements) {
						el.focus();
						if (document.activeElement === el) return;
					}
				}}
			>
				Skip to main content
			</button>

			<main className="app">
				<QueryClientProvider client={queryClient}>
					<Layout>
						<Component {...pageProps} />
					</Layout>
					<ReactQueryDevtools initialIsOpen={false} />
				</QueryClientProvider>
			</main>
		</>
	);
}

export default CustomApp;
